/**
 * Lightweight venue picker for adding / changing a stop's winery. Reuses the
 * existing `trpc.venue.search` procedure (do NOT rebuild venue search) — same
 * query the SearchPage uses. Modal, keyboard-dismissible, ≥44×44 targets.
 */

import { useState } from 'react';
import { trpc } from '../../lib/trpc';
import { Heading4, BodyText, Caption } from '../ui/Typography';
import { Button } from '../ui/Button';

interface VenuePickerModalProps {
  title: string;
  onPick: (venue: { id: string; name: string }) => void;
  onClose: () => void;
}

export const VenuePickerModal = ({
  title,
  onPick,
  onClose,
}: VenuePickerModalProps) => {
  const [term, setTerm] = useState('');
  const trimmed = term.trim();

  const search = trpc.venue.search.useQuery(
    { query: trimmed, limit: 10, status: 'published' },
    { enabled: trimmed.length >= 2 }
  );

  const handleBackdrop = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 p-4 pt-16"
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-full max-w-lg rounded-lg bg-gray-50 shadow-xl">
        <div className="flex items-center justify-between border-b border-[#DBD4C9] p-4">
          <Heading4>{title}</Heading4>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            aria-label="Close venue picker"
          >
            Close
          </Button>
        </div>

        <div className="p-4">
          <input
            type="text"
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
            }}
            placeholder="Search wineries by name…"
            className="w-full rounded-lg border border-[#DBD4C9] bg-gray-50 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info-500"
            aria-label="Search wineries"
          />

          <div className="mt-4 max-h-80 overflow-y-auto">
            {trimmed.length < 2 && (
              <Caption className="text-gray-500">
                Type at least 2 characters to search.
              </Caption>
            )}
            {trimmed.length >= 2 && search.isLoading && (
              <Caption className="text-gray-500">Searching…</Caption>
            )}
            {search.isError && (
              <Caption className="text-error">
                Couldn&apos;t search wineries. Try again.
              </Caption>
            )}
            {search.data?.items.length === 0 && (
              <Caption className="text-gray-500">
                No wineries match &ldquo;{trimmed}&rdquo;.
              </Caption>
            )}
            <ul className="space-y-2">
              {search.data?.items.map((v) => (
                <li key={v._id}>
                  <button
                    type="button"
                    onClick={() => onPick({ id: v._id, name: v.name })}
                    className="flex min-h-[44px] w-full flex-col items-start rounded-lg border border-[#DBD4C9] bg-gray-50 px-3 py-2 text-left hover:bg-[#F2F0EA] focus:outline-none focus:ring-2 focus:ring-info-500"
                  >
                    <BodyText className="text-gray-900">{v.name}</BodyText>
                    {(v.address.city ?? v.address.state) && (
                      <Caption className="text-gray-500">
                        {[v.address.city, v.address.state]
                          .filter(Boolean)
                          .join(', ')}
                      </Caption>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
